import React, { useState, useEffect } from 'react';
import { creditService } from '../../services/creditService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Search as SearchIcon,
  Savings as SavingsIcon,
  People as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

const CreditManagementPage = () => {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    customersWithCredit: 0,
    totalCreditAmount: 0,
    averageCredit: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (searchTerm.trim()) {
      const timer = setTimeout(() => {
        handleSearch();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      loadData();
    }
  }, [searchTerm]);

  const loadData = async () => {
    setLoading(true);
    const [customersResult, statsResult] = await Promise.all([
      creditService.getCustomersWithCredit(),
      creditService.getCreditStats()
    ]);

    if (customersResult.success) {
      setCustomers(customersResult.data);
    } else {
      setError(customersResult.error);
    }

    if (statsResult.success) {
      setStats(statsResult.data);
    }

    setLoading(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    const result = await creditService.searchCustomers(searchTerm);
    if (result.success) {
      setCustomers(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setCreditAmount(parseFloat(customer.credit_balance || 0).toFixed(2));
    setRemarks('');
    setShowEditModal(true);
  };

  const handleUpdateCredit = async () => {
    if (!selectedCustomer || creditAmount === '') {
      setError('Please enter a credit amount');
      return;
    }

    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount < 0) {
      setError('Please enter a valid amount (0 or greater)');
      return;
    }

    setLoading(true);
    const result = await creditService.updateCredit(
      selectedCustomer.customerid,
      amount,
      remarks
    );

    if (result.success) {
      setSuccess('Credit updated successfully!');
      setShowEditModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="xl" sx={{ p: 0 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SavingsIcon color="primary" /> Credit Management
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="info.dark" gutterBottom>Total Customers</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon color="info" />
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {stats.totalCustomers.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.lighter', border: '1px solid', borderColor: 'success.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="success.dark" gutterBottom>Customers with Credit</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WalletIcon color="success" />
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.customersWithCredit.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.lighter', border: '1px solid', borderColor: 'warning.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="warning.dark" gutterBottom>Total Credit Amount</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color="warning" />
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {formatCurrency(stats.totalCreditAmount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'primary.lighter', border: '1px solid', borderColor: 'primary.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="primary.dark" gutterBottom>Average Credit</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" />
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {formatCurrency(stats.averageCredit)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search Box */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search customer by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button 
                          variant="contained" 
                          size="small"
                          onClick={handleSearch}
                          disabled={loading}
                        >
                          Search
                        </Button>
                      </InputAdornment>
                    )
                  }}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Customers Table */}
        <Card variant="outlined">
          <CardContent sx={{ p: 0 }}>
            {loading && customers.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : customers.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <SavingsIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">No customers found</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                      <TableCell>Customer ID</TableCell>
                      <TableCell>Customer Name</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Barangay</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Credit Balance</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.customerid} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>{customer.customerid}</TableCell>
                        <TableCell>{customer.name}</TableCell>
                        <TableCell>{customer.address || ''}</TableCell>
                        <TableCell>{customer.barangay}</TableCell>
                        <TableCell><Chip label={customer.type} size="small" variant="outlined" /></TableCell>
                        <TableCell>
                          <Chip 
                            label={customer.status} 
                            size="small" 
                            color={customer.status === 'Active' ? 'success' : 'warning'} 
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {formatCurrency(customer.credit_balance)}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => openEditModal(customer)}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Edit Credit Modal */}
        <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon /> Edit Credit - {selectedCustomer?.name}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Customer:</strong> {selectedCustomer?.name} (ID: {selectedCustomer?.customerid})
              </Alert>

              <TextField
                label="Credit Amount"
                type="number"
                fullWidth
                required
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₱</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 }
                }}
                helperText="Enter the new credit balance for this customer"
                sx={{ mb: 3 }}
                autoFocus
              />

              <TextField
                label="Remarks (Optional)"
                fullWidth
                multiline
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Enter remarks..."
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button 
              variant="contained" 
              color="success" 
              onClick={handleUpdateCredit}
              disabled={loading}
              startIcon={<CheckIcon />}
            >
              Update Credit
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default CreditManagementPage;
